// F-V (2026-10-05): the Exhibits list's Narration column. The column header
// carries the "TC EN SC" labels; each row shows three badges in fixed-width
// slots so they line up under their label (a lane per language). Every badge
// — including "no description" — is a hoverable symbol whose tooltip starts
// with the language, so it stays readable once the header scrolls away.

import type { FC } from "react";
import { Info } from "lucide-react";
import type { NarrationStatus } from "@shared/api/narration";

export const NARRATION_LANGS = ["tc", "en", "sc"] as const;
export type NarrationLang = (typeof NARRATION_LANGS)[number];

export const LANG_LABEL: Record<NarrationLang, string> = {
  tc: "TC",
  en: "EN",
  sc: "SC",
};

export const BADGE: Record<
  NarrationStatus,
  { symbol: string; className: string; meaning: string }
> = {
  current: {
    symbol: "✓",
    className: "text-green-600",
    meaning: "AI narration audio, up to date",
  },
  stale: {
    symbol: "⟳",
    className: "text-amber-600",
    meaning: "description changed since this audio was generated",
  },
  custom: {
    symbol: "♪",
    className: "text-blue-600",
    meaning: "custom audio (never replaced by batch)",
  },
  missing: {
    symbol: "+",
    className: "text-zinc-500 font-semibold",
    meaning: "no narration audio yet. Can be generated",
  },
  none: {
    symbol: "∅",
    className: "text-zinc-300",
    meaning: "no description, so nothing to generate",
  },
};

const SLOT = "inline-flex w-7 justify-center";

/** Column header: "Narration ⓘ" over the three language labels. */
export const NarrationHeader: FC = () => (
  <div className="inline-flex flex-col items-start">
    <span className="inline-flex items-center gap-1">
      Narration
      <span className="relative group" data-testid="narration-legend">
        <Info
          className="w-3.5 h-3.5 text-muted-foreground"
          aria-label="Narration badge legend"
        />
        <span
          role="tooltip"
          className="pointer-events-none absolute right-0 top-5 z-30 hidden w-72 rounded-md border bg-white p-2 text-xs font-normal shadow-lg group-hover:block"
        >
          {(Object.keys(BADGE) as NarrationStatus[]).map((s) => (
            <span key={s} className="flex gap-2 py-0.5">
              <span className={`${SLOT} ${BADGE[s].className}`}>
                {BADGE[s].symbol}
              </span>
              <span className="text-foreground">{BADGE[s].meaning}</span>
            </span>
          ))}
        </span>
      </span>
    </span>
    <span className="text-xs text-muted-foreground font-mono">
      {NARRATION_LANGS.map((l) => (
        <span key={l} className={SLOT}>
          {LANG_LABEL[l]}
        </span>
      ))}
    </span>
  </div>
);

export interface NarrationBadgesProps {
  ownerId: string;
  status: Partial<Record<NarrationLang, NarrationStatus>> | null | undefined;
  /** Opens the exhibit on its Narration tab. */
  onOpen: (ownerId: string) => void;
}

const NarrationBadges: FC<NarrationBadgesProps> = ({
  ownerId,
  status,
  onOpen,
}) => (
  <span className="inline-flex font-mono text-base">
    {NARRATION_LANGS.map((lang) => {
      const s = status?.[lang];
      if (!s) return <span key={lang} className={SLOT} />;
      const badge = BADGE[s];
      return (
        <button
          key={lang}
          type="button"
          className={`${SLOT} ${badge.className} hover:scale-125 transition-transform`}
          title={`${LANG_LABEL[lang]}: ${badge.meaning}`}
          aria-label={`${LANG_LABEL[lang]}: ${badge.meaning}`}
          onClick={() => onOpen(ownerId)}
          data-testid={`narration-badge-${ownerId}-${lang}`}
          data-status={s}
        >
          {badge.symbol}
        </button>
      );
    })}
  </span>
);

export default NarrationBadges;
